import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Zap, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { loadRazorpayScript } from '../../lib/razorpay';

const API = API_BASE;

// Monthly AI credit meter + one-time top-up purchase (Phase 2 Tasks 1 & 3)
export const AICreditsCard = ({ workspaceId, workspaceName }) => {
  const [data, setData] = useState(null);
  const [buying, setBuying] = useState(false);

  const fetchCredits = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/workspaces/${workspaceId}/credits`, {
        headers: getAuthHeaders(),
      });
      setData(res.data);
    } catch (e) {
      console.error('Failed to load AI credits:', e);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId) fetchCredits();
  }, [workspaceId, fetchCredits]);

  const credits = data?.credits;
  if (!credits?.applicable) return null;

  const total = credits.total || 0;
  const used = credits.used || 0;
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const exhausted = (credits.remaining || 0) <= 0;

  const handleTopup = async () => {
    setBuying(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setBuying(false);
        return;
      }
      const orderRes = await axios.post(
        `${API}/workspaces/${workspaceId}/credits/topup/create-order`,
        {},
        { headers: getAuthHeaders() }
      );
      const order = orderRes.data;
      const rzp = new window.Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency,
        name: 'FrameFlow',
        description: `AI Credit Top-up (${order.credits} credits)`,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${API}/workspaces/${workspaceId}/credits/topup/verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: getAuthHeaders() }
            );
            if (verifyRes.data.status === 'success') {
              toast.success(`${order.credits} AI credits added to ${workspaceName || 'your workspace'}`);
              setData((prev) => ({ ...prev, credits: verifyRes.data.credits }));
            }
          } catch (e) {
            toast.error('Payment verification failed — if you were charged, the credits will land automatically within a minute.');
          }
          setBuying(false);
        },
        modal: { ondismiss: () => setBuying(false) },
        theme: { color: '#F59E0B' },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start top-up');
      setBuying(false);
    }
  };

  return (
    <Card className="p-4" data-testid="ai-credits-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Zap className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-semibold">AI Credits</p>
            <p className="text-sm text-muted-foreground">
              1 credit per AI call · resets each billing cycle
            </p>
          </div>
        </div>
        {data?.is_owner && data?.topup?.configured && (
          <Button
            size="sm"
            variant={exhausted ? 'default' : 'outline'}
            onClick={handleTopup}
            disabled={buying}
            data-testid="ai-credits-topup-btn"
          >
            {buying ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Processing…</>
            ) : (
              <><Plus className="h-4 w-4 mr-1.5" />Buy {data.topup.credits} credits — ₹{Math.round(data.topup.price_inr)}</>
            )}
          </Button>
        )}
      </div>

      <div className="mt-4">
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-sm font-medium" data-testid="ai-credits-usage-text">
            {used.toLocaleString()} of {total.toLocaleString()} used
          </span>
          <span
            className={`text-sm font-semibold ${exhausted ? 'text-destructive' : 'text-muted-foreground'}`}
            data-testid="ai-credits-remaining"
          >
            {(credits.remaining || 0).toLocaleString()} left
          </span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden" data-testid="ai-credits-bar">
          <div
            className={`h-full rounded-full transition-all ${exhausted ? 'bg-destructive' : 'bg-primary'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1.5">
          {credits.allowance?.toLocaleString()} monthly allowance
          {credits.purchased > 0 && ` + ${credits.purchased.toLocaleString()} purchased this cycle`}
          {exhausted && ' — AI calls are paused until you top up or the cycle resets.'}
        </p>
      </div>
    </Card>
  );
};

export default AICreditsCard;
